import { BlogArticle } from '../types';

// DSE English Paper 2 寫作卷攻略 → CTA: /module/writing
const article: BlogArticle = {
  slug: 'dse-english-paper2-writing-guide',
  date: '2026-08-25',
  readingMinutes: 6,
  tags: ['DSE', '英文', 'Writing', '考試攻略'],

  locales: {
    zhHant: {
      title: 'DSE English Paper 2 寫作卷完整攻略',
      excerpt: 'Paper 2 佔英文科 25%，但好多同學連佢點評分都唔知。一文睇清卷面結構、評分準則、時間分配同常見題型。',
      keywords: 'DSE English, Paper 2, 寫作卷, DSE英文作文, HKDSE',
      contentHtml: `
        <p>DSE English Paper 2（Writing）佔全科成績 25%，係性價比最高嘅一卷 —— 因為寫作係最可以靠「準備」提升嘅部分。但好多同學連份卷點結構、點評分都未搞清楚就入場。呢篇一次過講清楚。</p>

        <h2>卷面結構</h2>
        <table>
          <thead>
            <tr><th>部分</th><th>內容</th><th>字數</th><th>比重</th></tr>
          </thead>
          <tbody>
            <tr><td>Part A</td><td>指定任務（如 email、report、proposal）</td><td>約 200 字</td><td>約 40%</td></tr>
            <tr><td>Part B</td><td>四選一長文（article、letter、speech 等）</td><td>約 400 字</td><td>約 60%</td></tr>
          </tbody>
        </table>
        <p>全卷 2 小時。建議時間分配：<strong>Part A 約 45 分鐘，Part B 約 70 分鐘，預留 5 分鐘檢查</strong>。</p>

        <h2>三大評分準則</h2>
        <ol>
          <li><strong>Content（內容）</strong>：有冇答中題目要求嘅所有要點（task fulfilment）。<em>漏一個 bullet point 就係實扣分。</em></li>
          <li><strong>Language（語文）</strong>：文法、串字、句式多樣性。錯誤唔使零，但要少到唔影響理解。</li>
          <li><strong>Organisation（組織）</strong>：段落清晰、連接詞恰當、格式啱（letter 有 letter 嘅格式，report 有 report 嘅格式）。</li>
        </ol>

        <div class="tip-box">
          <strong>💡 貼士：</strong>Content 係最容易攞滿嘅一項 —— 落筆前用 3 分鐘將題目嘅要求逐點圈出，寫作時逐點對住嚟答，就唔會漏。
        </div>

        <h2>Part A 常見題型</h2>
        <ul>
          <li><strong>Email / Letter</strong>：注意開頭稱呼同結尾（Yours sincerely / faithfully 嘅分別）</li>
          <li><strong>Report</strong>：要有標題同小標題，語氣正式</li>
          <li><strong>Proposal</strong>：結構係「問題 → 建議 → 預期效果」</li>
        </ul>

        <h2>Part B 高分策略</h2>
        <ul>
          <li><strong>揀題唔好貪得意</strong>：揀你最有 point、最有例子嘅題目，唔係最有趣嘅題目</li>
          <li><strong>開段直接入題</strong>：唔好寫一大段引言先講重點，考官冇時間搵你嘅立場</li>
          <li><strong>每段一個論點</strong>：主題句開頭，跟住解釋 + 例子</li>
          <li><strong>格式要啱</strong>：speech 要有呼籲同聽眾互動；article 要有吸睛標題</li>
        </ul>

        <h2>最常見嘅失分位</h2>
        <table>
          <thead>
            <tr><th>失分位</th><th>點樣避免</th></tr>
          </thead>
          <tbody>
            <tr><td>漏答題目要點</td><td>落筆前圈出所有 bullet points</td></tr>
            <tr><td>格式錯（如 report 冇標題）</td><td>背熟各文體格式</td></tr>
            <tr><td>時態/主謂一致錯誤</td><td>預留 5 分鐘專檢查呢兩樣</td></tr>
            <tr><td>字數嚴重不足</td><td>計時練習，掌握自己嘅寫作速度</td></tr>
          </tbody>
        </table>

        <h2>點樣練習最有效？</h2>
        <p>寫完冇人改，等於唔知自己練成點。AI 作文批改可以按 DSE 評分準則即時俾分，指出 Content / Language / Organisation 各方面嘅問題，仲會俾埋修訂版：</p>
        <p><a class="blog-cta-button" href="/module/writing">✍️ 免費批改 DSE 英文作文 →</a></p>

        <blockquote>Paper 2 係最可以靠準備攞分嘅一卷 —— 了解遊戲規則，你已經贏咗一半。</blockquote>
      `,
    },

    zhHans: {
      title: 'DSE English Paper 2 写作卷完整攻略',
      excerpt: 'Paper 2 占英文科 25%，但很多同学连它怎么评分都不知道。一文看清卷面结构、评分准则、时间分配和常见题型。',
      keywords: 'DSE English, Paper 2, 写作卷, DSE英文作文, HKDSE',
      contentHtml: `
        <p>DSE English Paper 2（Writing）占全科成绩 25%，是性价比最高的一卷 —— 因为写作是最可以靠「准备」提升的部分。但很多同学连这份卷怎么结构、怎么评分都没搞清楚就进场。这篇一次过讲清楚。</p>

        <h2>卷面结构</h2>
        <table>
          <thead>
            <tr><th>部分</th><th>内容</th><th>字数</th><th>比重</th></tr>
          </thead>
          <tbody>
            <tr><td>Part A</td><td>指定任务（如 email、report、proposal）</td><td>约 200 字</td><td>约 40%</td></tr>
            <tr><td>Part B</td><td>四选一长文（article、letter、speech 等）</td><td>约 400 字</td><td>约 60%</td></tr>
          </tbody>
        </table>
        <p>全卷 2 小时。建议时间分配：<strong>Part A 约 45 分钟，Part B 约 70 分钟，预留 5 分钟检查</strong>。</p>

        <h2>三大评分准则</h2>
        <ol>
          <li><strong>Content（内容）</strong>：有没有答中题目要求的所有要点（task fulfilment）。<em>漏一个 bullet point 就是实扣分。</em></li>
          <li><strong>Language（语文）</strong>：语法、拼字、句式多样性。错误不用零，但要少到不影响理解。</li>
          <li><strong>Organisation（组织）</strong>：段落清晰、连接词恰当、格式对（letter 有 letter 的格式，report 有 report 的格式）。</li>
        </ol>

        <div class="tip-box">
          <strong>💡 提示：</strong>Content 是最容易拿满的一项 —— 动笔前用 3 分钟把题目的要求逐点圈出，写作时逐点对着来答，就不会漏。
        </div>

        <h2>Part A 常见题型</h2>
        <ul>
          <li><strong>Email / Letter</strong>：注意开头称呼和结尾（Yours sincerely / faithfully 的分别）</li>
          <li><strong>Report</strong>：要有标题和小标题，语气正式</li>
          <li><strong>Proposal</strong>：结构是「问题 → 建议 → 预期效果」</li>
        </ul>

        <h2>Part B 高分策略</h2>
        <ul>
          <li><strong>选题不要贪新鲜</strong>：选你最有观点、最有例子的题目，不是最有趣的题目</li>
          <li><strong>开段直接入题</strong>：不要写一大段引言才讲重点，考官没时间找你的立场</li>
          <li><strong>每段一个论点</strong>：主题句开头，接着解释 + 例子</li>
          <li><strong>格式要对</strong>：speech 要有呼吁和听众互动；article 要有吸睛标题</li>
        </ul>

        <h2>最常见的失分点</h2>
        <table>
          <thead>
            <tr><th>失分点</th><th>怎样避免</th></tr>
          </thead>
          <tbody>
            <tr><td>漏答题目要点</td><td>动笔前圈出所有 bullet points</td></tr>
            <tr><td>格式错（如 report 没标题）</td><td>背熟各文体格式</td></tr>
            <tr><td>时态/主谓一致错误</td><td>预留 5 分钟专检查这两样</td></tr>
            <tr><td>字数严重不足</td><td>计时练习，掌握自己的写作速度</td></tr>
          </tbody>
        </table>

        <h2>怎样练习最有效？</h2>
        <p>写完没人改，等于不知道自己练得怎样。AI 作文批改可以按 DSE 评分准则即时给分，指出 Content / Language / Organisation 各方面的问题，还会给修订版：</p>
        <p><a class="blog-cta-button" href="/module/writing">✍️ 免费批改 DSE 英文作文 →</a></p>

        <blockquote>Paper 2 是最可以靠准备拿分的一卷 —— 了解游戏规则，你已经赢了一半。</blockquote>
      `,
    },

    en: {
      title: 'DSE English Paper 2: The Complete Writing Guide',
      excerpt: "Paper 2 is worth 25% of your English grade, yet many students walk in without knowing how it's marked. Structure, criteria, timing and question types — all in one guide.",
      keywords: 'DSE English, Paper 2, writing paper, HKDSE, essay writing',
      contentHtml: `
        <p>DSE English Paper 2 (Writing) is worth 25% of your grade — and it's the most prepare-able paper, because writing improves fastest with deliberate practice. Yet many candidates walk in without knowing how the paper is structured or marked. This guide fixes that.</p>

        <h2>Paper structure</h2>
        <table>
          <thead>
            <tr><th>Part</th><th>Task</th><th>Length</th><th>Weight</th></tr>
          </thead>
          <tbody>
            <tr><td>Part A</td><td>Set task (email, report, proposal…)</td><td>~200 words</td><td>~40%</td></tr>
            <tr><td>Part B</td><td>1 of 4 extended tasks (article, letter, speech…)</td><td>~400 words</td><td>~60%</td></tr>
          </tbody>
        </table>
        <p>You have 2 hours. A solid split: <strong>45 min for Part A, 70 min for Part B, 5 min to check</strong>.</p>

        <h2>The three marking criteria</h2>
        <ol>
          <li><strong>Content</strong>: did you hit every required point (task fulfilment)? <em>Every missed bullet point is a guaranteed mark lost.</em></li>
          <li><strong>Language</strong>: grammar, spelling, sentence variety. Errors don't need to be zero — just rare enough not to impede understanding.</li>
          <li><strong>Organisation</strong>: clear paragraphs, appropriate linking, and correct format (a letter looks like a letter; a report looks like a report).</li>
        </ol>

        <div class="tip-box">
          <strong>💡 Tip:</strong> Content is the easiest criterion to max out — spend 3 minutes circling every requirement in the prompt before writing, then answer them one by one. No more missed points.
        </div>

        <h2>Common Part A task types</h2>
        <ul>
          <li><strong>Email / Letter</strong>: get the salutation and sign-off right (Yours sincerely vs faithfully)</li>
          <li><strong>Report</strong>: title + subheadings, formal tone</li>
          <li><strong>Proposal</strong>: structure = problem → suggestion → expected outcome</li>
        </ul>

        <h2>Part B strategies for higher bands</h2>
        <ul>
          <li><strong>Don't pick the "interesting" question</strong> — pick the one where you have the strongest points and examples</li>
          <li><strong>Get to the point immediately</strong> — examiners won't dig through a long introduction for your stance</li>
          <li><strong>One argument per paragraph</strong>: topic sentence first, then explanation + example</li>
          <li><strong>Match the format</strong>: a speech needs audience engagement; an article needs a catchy headline</li>
        </ul>

        <h2>The most common mark-losers</h2>
        <table>
          <thead>
            <tr><th>Mark-loser</th><th>Fix</th></tr>
          </thead>
          <tbody>
            <tr><td>Missing required points</td><td>Circle all bullet points before writing</td></tr>
            <tr><td>Wrong format (e.g. report with no title)</td><td>Memorise each text type's conventions</td></tr>
            <tr><td>Tense / agreement errors</td><td>Reserve 5 minutes to check just these two</td></tr>
            <tr><td>Severely under length</td><td>Timed practice to learn your writing speed</td></tr>
          </tbody>
        </table>

        <h2>The most effective way to practise</h2>
        <p>Writing without feedback means never knowing if you're improving. AI essay grading scores your work instantly against DSE criteria — Content, Language, Organisation — with a mistake list and a revised version:</p>
        <p><a class="blog-cta-button" href="/module/writing">✍️ Grade my DSE essay for free →</a></p>

        <blockquote>Paper 2 is the most prepare-able paper there is — know the rules of the game, and you're halfway to winning it.</blockquote>
      `,
    },
  },
};

export default article;
